import { remove } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UpdateFavoriteWorkplacesAnalytics } from "@src/appV2/redesign/ShiftDiscovery/FavoriteWorkplaces/types";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import { getFavoriteWorkplacesBaseQueryKey } from "./useGetFavoriteWorkplaces";

const removeFavoriteWorkplaceResponseSchema = z.string();

export type RemoveFavoriteWorkplaceResponse = z.infer<typeof removeFavoriteWorkplaceResponseSchema>;

export interface RemoveFavoriteWorkplaceParams {
  favoriteId: string;
  workplaceId: string;
  analytics: UpdateFavoriteWorkplacesAnalytics;
}

export function useRemoveFavoriteWorkplace(
  options: UseMutationOptions<
    RemoveFavoriteWorkplaceResponse,
    AxiosError,
    RemoveFavoriteWorkplaceParams
  > = {}
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: RemoveFavoriteWorkplaceParams) => {
      const response = await remove({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/favorite-workplaces/${params.favoriteId}`,
        responseSchema: removeFavoriteWorkplaceResponseSchema,
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.REMOVE_FAVORITE_WORKPLACE_FAILURE,
      userErrorMessage: "Failed to remove workplace from favorites",
    },

    ...options,
    onSettled: (...params) => {
      // We don't want anyone to override this, so we call it before the custom onSettled
      void queryClient.invalidateQueries({
        queryKey: getFavoriteWorkplacesBaseQueryKey(),
      });
      return options.onSettled?.(...params);
    },
  });
}
